import type { BoxProps } from '@hover/blueprint';
import { Box } from '@hover/blueprint';
import type {
  MutableRefObject,
  TouchEventHandler,
  TransitionEventHandler,
} from 'react';
import React, { useMemo, useRef, useState } from 'react';

import { MaximizeButton } from 'src/modules/renderings/components/common/MaximizeButton';
import { MinimizeButton } from 'src/modules/renderings/components/common/MinimizeButton';
import { useFocusedView } from 'src/modules/renderings/hooks/useFocusedView';
import type { ImageLike } from 'src/modules/renderings/imageLike';

import { AspectRatioBox } from './AspectRatioBox';
import { CarouselImages } from './CarouselImages';
import { CarouselOverlay } from './CarouselOverlay';
import { CarouselTransform } from './CarouselTransform';
import { ImageNavigationButton } from './ImageNavigationButton';

const SWIPE_THRESHOLD = 0.2;

const SWIPE_TRANSITION_MS = 150;
const SWIPE_DEBOUNCE_MS = 17; // roughly allow an update 60 times a second
const ASPECT_RATIO_PERCENT = '56.25%'; // 16:9

type TransitionFromTo = [number | null, number | null];

/**
 * Gets a single touch matching the provided identifier from a TouchList
 */
const getTouchByIdentifier = (touches: TouchList, identifier: number) => {
  for (let i = 0; i < touches.length; i += 1) {
    if (touches[i].identifier === identifier) {
      return touches[i];
    }
  }

  return undefined;
};

/**
 * Binds the x-axis change to +- the width of the image element
 */
const calcMaxClientXChange = (
  el: MutableRefObject<HTMLImageElement | null>,
  originalClientX: number,
  currentClientX: number,
) =>
  Math.max(
    (el.current?.clientWidth ?? 0) * -1,
    Math.min(el.current?.clientWidth ?? 0, originalClientX - currentClientX),
  );

/**
 * Calculates the x-axis translation percentage display a given index in the
 * carousel
 */
const calcTranslatePercent = (idx: number) => `-${(idx + 1) * 100}%`;

type CarouselProps = {
  selectedIndex: number;
  images: ImageLike[];
  onSwipe?: (idx: number) => void;
  testId?: string;
  maxHeight?: BoxProps['maxHeight'];
};

const Carousel = ({
  selectedIndex,
  images,
  onSwipe = () => undefined,
  testId,
  maxHeight,
}: CarouselProps) => {
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null);
  const [swipeChangeX, setSwipeChangeX] = useState<number>(0);
  const [swipeHoldX, setSwipeHoldX] = useState<number>(0);
  const [trackedTouchId, setTrackedTouchId] = useState<number>(0);
  const [transitionFromTo, setTransitionFromTo] = useState<TransitionFromTo>([
    null,
    null,
  ]);
  const [isAttachingTransition, setIsAttachingTransition] =
    useState<boolean>(false);

  const primaryImageRef = useRef<HTMLImageElement>(null);
  const primaryIndex = transitionFromTo[1] ?? selectedIndex;
  const [transitionFrom, transitionTo] = transitionFromTo;
  const isTransitioning = transitionFromTo.every(
    val => typeof val === 'number',
  );

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = evt => {
    const touchIdToTrack = evt.nativeEvent.touches[0]?.identifier ?? 0;
    const touch = getTouchByIdentifier(evt.nativeEvent.touches, touchIdToTrack);
    if (touch) {
      setTrackedTouchId(touchIdToTrack);
      setSwipeStartX(touch.clientX);
    }
  };

  const handleTouchMove = useMemo(() => {
    let debounceFrom = 0;
    const debounce = () => {
      if (Date.now() - debounceFrom < SWIPE_DEBOUNCE_MS) {
        return true;
      }
      debounceFrom = Date.now();
      return false;
    };

    const handler: TouchEventHandler<HTMLImageElement> = evt => {
      if (debounce()) {
        return;
      }

      const touch = getTouchByIdentifier(
        evt.nativeEvent.changedTouches,
        trackedTouchId,
      );

      if (!touch) {
        return;
      }
      const clientXChange = calcMaxClientXChange(
        primaryImageRef,
        swipeStartX ?? 0,
        touch?.clientX ?? 0,
      );

      setSwipeChangeX(-1 * clientXChange);
    };

    return handler;
  }, [swipeStartX, trackedTouchId]);

  const handleTouchEnd: TouchEventHandler<HTMLDivElement> = evt => {
    const touch = getTouchByIdentifier(
      evt.nativeEvent.changedTouches,
      trackedTouchId,
    );
    const clientXChange = calcMaxClientXChange(
      primaryImageRef,
      swipeStartX ?? 0,
      touch?.clientX ?? 0,
    );

    const ratioChange = Math.abs(
      clientXChange / (primaryImageRef.current?.clientWidth ?? 1),
    );

    const isSwiped = ratioChange > SWIPE_THRESHOLD;
    setSwipeHoldX(swipeChangeX);
    setIsAttachingTransition(true);
    setSwipeChangeX(0);
    setTrackedTouchId(0);
    setSwipeStartX(null);

    if (isSwiped && clientXChange > 0) {
      // Delay onSwipe call to allow transition css to be attached
      setTransitionFromTo([selectedIndex, selectedIndex + 1]);
      setTimeout(() => {
        setIsAttachingTransition(false);
      }, 0);
    } else if (isSwiped && clientXChange < 0) {
      // Delay onSwipe call to allow transition css to be attached
      setTransitionFromTo([selectedIndex, selectedIndex - 1]);
      setTimeout(() => {
        setIsAttachingTransition(false);
      }, 0);
    } else {
      setTimeout(() => setIsAttachingTransition(false), 0);
      setTransitionFromTo([selectedIndex, selectedIndex]);
    }
  };

  const handleTransitionEnd: TransitionEventHandler = () => {
    if (transitionFrom !== transitionTo && typeof transitionTo === 'number') {
      if (transitionTo === -1) {
        // a transitionTo value of -1 represents looping around to the end of
        // the images array
        onSwipe(images.length - 1);
      } else if (transitionTo === images.length) {
        // a transitionTo value equal to images.length represents looping
        // around to the beginning of the images array
        onSwipe(0);
      } else {
        onSwipe(transitionTo);
      }
    }
    setTransitionFromTo([null, null]);
  };

  let transform;

  if (isTransitioning && isAttachingTransition) {
    // Hold our last swipeChangeX while we attach transition css
    const translatePercent = calcTranslatePercent(transitionFrom!);
    const translatePx = `${swipeHoldX}px`;
    transform = `translate(calc(${translatePercent} + ${translatePx}), 0)`;
  } else if (isTransitioning) {
    // Translate to selected index position, ignoring any active swipe
    const translatePercent = calcTranslatePercent(transitionTo!);
    transform = `translate(${translatePercent}, 0)`;
  } else {
    // Translate to selected index position, adjusted for any active swipe
    const translatePercent = calcTranslatePercent(primaryIndex);
    const translatePx = `${swipeChangeX}px`;
    transform = `translate(calc(${translatePercent} + ${translatePx}), 0)`;
  }

  const transition = isTransitioning
    ? `transform ${SWIPE_TRANSITION_MS}ms linear`
    : undefined;

  const { toggleFocusedView, isFocusedView } = useFocusedView();
  const MinMaxButton = isFocusedView ? MinimizeButton : MaximizeButton;
  const isSwipeEnabled = !isTransitioning && isFocusedView;

  return (
    <Box display="block" maxHeight={maxHeight}>
      <AspectRatioBox
        maxHeight={maxHeight}
        ratioAsPercent={ASPECT_RATIO_PERCENT}
        testId={testId}
      >
        <CarouselTransform
          onTransitionEnd={handleTransitionEnd}
          transform={transform}
          transition={transition}
        >
          <CarouselImages
            images={images}
            onTouchEnd={isSwipeEnabled ? handleTouchEnd : undefined}
            onTouchMove={isSwipeEnabled ? handleTouchMove : undefined}
            onTouchStart={isSwipeEnabled ? handleTouchStart : undefined}
            selectedImageRef={primaryImageRef}
            selectedIndex={primaryIndex}
          />
        </CarouselTransform>
        <CarouselOverlay images={images} selectedIndex={primaryIndex} />
      </AspectRatioBox>
      {isFocusedView && (
        <ImageNavigationButton
          direction="left"
          onClick={() => setTransitionFromTo([primaryIndex, primaryIndex - 1])}
        />
      )}
      {isFocusedView && (
        <ImageNavigationButton
          direction="right"
          onClick={() => setTransitionFromTo([primaryIndex, primaryIndex + 1])}
        />
      )}
      <Box padding={400} position="absolute" right={0} top={0}>
        <MinMaxButton onClick={toggleFocusedView} />
      </Box>
    </Box>
  );
};

export { Carousel };
